<template>
  <div id="app" class="min-h-screen bg-gray-50 flex flex-col" @mousemove="handleMouseMove">
    <main class="flex-grow">
      <router-view />
    </main>
    <Footer />
    <Toast />

    <!-- Global Loading Overlay -->
    <BRKLoading
      :show="globalLoading.show"
      :title="globalLoading.title"
      :message="globalLoading.message"
      :progress="globalLoading.progress"
      :show-progress="globalLoading.showProgress"
    />

    <!-- Global Confirmation Dialog -->
    <BRKConfirm
      :show="globalConfirm.show"
      :type="globalConfirm.type"
      :title="globalConfirm.title"
      :message="globalConfirm.message"
      :html="globalConfirm.html"
      :confirm-text="globalConfirm.confirmText"
      :cancel-text="globalConfirm.cancelText"
      :processing="globalConfirm.processing"
      :processing-text="globalConfirm.processingText"
      :focus-cancel="globalConfirm.focusCancel"
      @confirm="handleConfirm"
      @cancel="handleCancel"
    />

    <!-- PWA Install Banner -->
    <Transition name="slide-up">
      <div v-if="showInstallBanner" class="pwa-install-banner">
        <div class="pwa-install-content">
          <div class="pwa-install-icon">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div class="pwa-install-text">
            <p class="font-semibold text-gray-900">ติดตั้งแอป HR-PRO</p>
            <p class="text-sm text-gray-500">เพิ่มลงหน้าจอหลักเพื่อใช้งานแบบแอป</p>
          </div>
        </div>
        <div class="pwa-install-actions">
          <button @click="dismissInstallBanner" class="pwa-btn-dismiss">ไว้ก่อน</button>
          <button @click="installPWA" class="pwa-btn-install">ติดตั้ง</button>
        </div>
      </div>
    </Transition>

    <!-- Mouse Ripple Container (Global) -->
    <div class="fixed inset-0 pointer-events-none" style="z-index: 99999;" ref="rippleContainer"></div>

    <!-- Ripple Effect Toggle Button -->
    <button
      @click="toggleRippleEffect"
      class="fixed right-6 w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
      :class="[
        rippleEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 hover:bg-gray-500',
        isMobileRoute ? 'bottom-24 z-[1001]' : 'bottom-6 z-50'
      ]"
      :title="rippleEnabled ? 'ปิดเอฟเฟกต์คลื่น' : 'เปิดเอฟเฟกต์คลื่น'"
    >
      <!-- Ripple Icon -->
      <svg
        class="w-6 h-6 text-white transition-transform duration-300"
        :class="rippleEnabled ? 'scale-110' : 'scale-100'"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="3" stroke-width="2" />
        <circle cx="12" cy="12" r="7" stroke-width="2" opacity="0.6" />
        <circle cx="12" cy="12" r="10" stroke-width="2" opacity="0.3" />
      </svg>

      <!-- Tooltip -->
      <div class="absolute bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
        {{ rippleEnabled ? 'ปิดเอฟเฟกต์คลื่น' : 'เปิดเอฟเฟกต์คลื่น' }}
        <div class="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Toast from '@/components/Toast.vue'
import Footer from '@/components/Footer.vue'
import { useLoading } from '@/composables/useLoading'
import { useConfirm } from '@/composables/useConfirm'

// Global composables
const { globalLoading } = useLoading()
const { globalConfirm, handleConfirm, handleCancel } = useConfirm()

const route = useRoute()

// Check if current route is mobile
const isMobileRoute = computed(() => route.path.startsWith('/m/'))

// PWA Install Prompt
const deferredPrompt = ref(null)
const showInstallBanner = ref(false)

const isPWAInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
}

const installPWA = async () => {
  if (!deferredPrompt.value) return
  deferredPrompt.value.prompt()
  const { outcome } = await deferredPrompt.value.userChoice
  if (outcome === 'accepted') {
    console.log('✅ PWA installed')
  }
  deferredPrompt.value = null
  showInstallBanner.value = false
}

const dismissInstallBanner = () => {
  showInstallBanner.value = false
  // ไม่โชว์อีก 7 วัน
  localStorage.setItem('pwa-install-dismissed', Date.now().toString())
}

// Mouse Ripple Effect
const rippleContainer = ref(null)
const rippleEnabled = ref(localStorage.getItem('rippleEffectEnabled') !== 'false') // Default: enabled
const lastRippleTime = ref(0)

// Toggle ripple effect
const toggleRippleEffect = () => {
  rippleEnabled.value = !rippleEnabled.value
  localStorage.setItem('rippleEffectEnabled', rippleEnabled.value.toString())
}

// Mouse Ripple Effect Handler
const handleMouseMove = (e) => {
  if (!rippleEnabled.value) return

  const now = Date.now()
  // Throttle: Create ripple only every 120ms
  if (now - lastRippleTime.value < 120) return

  lastRippleTime.value = now

  if (!rippleContainer.value) return

  // Create ripple element with inline styles
  const ripple = document.createElement('div')
  ripple.style.cssText = `
    position: fixed;
    left: ${e.clientX}px;
    top: ${e.clientY}px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.25) 0%, rgba(59, 130, 246, 0.15) 40%, transparent 70%);
    border: 1.5px solid rgba(59, 130, 246, 0.3);
    box-shadow: 0 0 12px rgba(59, 130, 246, 0.2);
    transform: translate(-50%, -50%) scale(0.5);
    pointer-events: none;
    z-index: 99999;
    animation: ripple-expand 1s ease-out forwards;
  `

  rippleContainer.value.appendChild(ripple)

  // Remove after animation completes
  setTimeout(() => {
    if (ripple && ripple.parentNode) {
      ripple.remove()
    }
  }, 1000)
}

onMounted(() => {
  // Initialize ripple effect setting from localStorage
  const savedSetting = localStorage.getItem('rippleEffectEnabled')
  if (savedSetting !== null) {
    rippleEnabled.value = savedSetting === 'true'
  }

  // PWA Install Prompt
  if (!isPWAInstalled()) {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      deferredPrompt.value = e

      // ตรวจว่า dismiss ไปเกิน 7 วันหรือยัง
      const dismissed = localStorage.getItem('pwa-install-dismissed')
      if (dismissed) {
        const daysSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24)
        if (daysSince < 7) return
      }

      showInstallBanner.value = true
    })

    window.addEventListener('appinstalled', () => {
      console.log('✅ HR-PRO installed successfully')
      showInstallBanner.value = false
      deferredPrompt.value = null
    })
  }
})
</script>

<style>
/* ===============================================
   🎨 GLOBAL RIPPLE ANIMATION
   =============================================== */

/* Ripple Expand Animation - Must be global for dynamically created elements */
/* ===============================================
   📱 PWA INSTALL BANNER
   =============================================== */

.pwa-install-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  padding: 16px 20px;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pwa-install-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.pwa-install-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  background: #eff6ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pwa-install-text {
  min-width: 0;
}

.pwa-install-text p {
  margin: 0;
  line-height: 1.3;
}

.pwa-install-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.pwa-btn-dismiss {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
}

.pwa-btn-dismiss:hover {
  background: #f3f4f6;
}

.pwa-btn-install {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  cursor: pointer;
}

.pwa-btn-install:hover {
  background: #1d4ed8;
}

/* Slide up transition */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@keyframes ripple-expand {
  0% {
    transform: translate(-50%, -50%) scale(0.5);
    opacity: 0.5;
  }
  20% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.25;
  }
  80% {
    opacity: 0.1;
  }
  100% {
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
  }
}
</style>
